import React, { useEffect, useState } from 'react';
import { NewsRunsLogic, type NewsRuns } from './news_runs_logic';
import { fieldLabelStyle } from './read/styles';
import { pxToRem } from '@/modules/config/constants';

interface NewsLeaderboardProps {
  runs: NewsRuns & { impressionGoal: number };
}

/**
 * The topic's news runs once they are worth ranking, or null — i.e. whether
 * {@link NewsLeaderboard} has anything to show. The engagement card owns the
 * list item the leaderboard renders into, so it has to know before it renders
 * one, which only the fetch can tell it.
 *
 * Pass no topicId to skip the fetch entirely (non-news topics never ask).
 *
 * @param refreshKey Bump to refetch (e.g. after returning to the view) — a
 * plain dependency, no imperative handle needed.
 */
export const useNewsRuns = (
  topicId?: string,
  refreshKey = 0
): (NewsRuns & { impressionGoal: number }) | null => {
  const [runs, setRuns] = useState<NewsRuns | null>(null);

  useEffect(() => {
    if (!topicId) {
      setRuns(null);
      return;
    }
    const controller = new AbortController();
    NewsRunsLogic.fetch(topicId, controller.signal)
      .then(setRuns)
      .catch(() => {
        // Leave the leaderboard hidden — it's informational, never blocking.
      });
    return () => controller.abort();
  }, [topicId, refreshKey]);

  // No race goal means nothing to rank.
  if (!runs || runs.impressionGoal == null) return null;
  return runs as NewsRuns & { impressionGoal: number };
};

/**
 * The news race leaderboard (#447): a run-count stat plus one row per
 * COMPLETED run (fastest first) — who started it, how fast it hit the goal,
 * and how many hands it passed through. In-flight runs count toward the stat
 * but earn no row until they cross the line; a friendly empty state stands in
 * while no run has finished yet.
 *
 * A body of the engagement card: the card supplies the list item, and
 * {@link useNewsRuns} tells it whether to render one at all.
 */
const NewsLeaderboard: React.FC<NewsLeaderboardProps> = ({ runs }) => {
  const cellStyle: React.CSSProperties = {
    padding: '6px 8px',
    textAlign: 'left',
    fontSize: pxToRem(14),
  };

  return (
    <div data-testid="news-leaderboard">
      <p style={fieldLabelStyle}>Racing to {runs.impressionGoal}</p>
      <p style={{ margin: '0 0 8px' }} data-testid="news-total-runs">
        {runs.totalRuns} {runs.totalRuns === 1 ? 'run' : 'runs'} so far —{' '}
        {runs.completedRuns.length} reached the goal
      </p>
      {runs.completedRuns.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse' }}
            data-testid="news-leaderboard-table"
          >
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd' }}>
                <th style={cellStyle}>Who</th>
                <th style={cellStyle}>Time to goal</th>
                <th style={cellStyle}>Degrees</th>
              </tr>
            </thead>
            <tbody>
              {runs.completedRuns.map(run => (
                <tr
                  key={run.startingLegId}
                  data-testid={`news-leaderboard-row-${run.startingLegId}`}
                >
                  <td style={cellStyle}>{NewsRunsLogic.senderName(run)}</td>
                  <td style={cellStyle}>
                    {NewsRunsLogic.formatTimeToGoal(run.timeToGoalSeconds)}
                  </td>
                  <td style={cellStyle}>{run.degreesOfSeparation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p
          style={{ margin: 0, fontSize: pxToRem(14), color: '#666' }}
          data-testid="news-leaderboard-empty"
        >
          No run has reached the goal yet — the leaderboard is wide open.
        </p>
      )}
    </div>
  );
};

export default NewsLeaderboard;
